import React, { useCallback, useEffect, useState } from 'react';
import { GalleryPhoto } from '../data/gallery';

const THUMB_BASE = '/assets/gallery/thumb';
const FULL_BASE = '/assets/gallery/full';

const captionFor = (photo: GalleryPhoto, index: number) =>
  `Art on the Lake — photo ${index + 1}`;

/** How many tiles load immediately before lazy loading takes over. */
const EAGER_COUNT = 12;

/**
 * A tile image that fades in once decoded. Without this, a screen of
 * not-yet-loaded tiles is a wall of flat coloured squares that reads as broken
 * images rather than as loading ones.
 */
const Thumb: React.FC<{ photo: GalleryPhoto; alt: string; eager: boolean }> = ({
  photo,
  alt,
  eager,
}) => {
  const [loaded, setLoaded] = useState(false);

  // An image served from cache can finish before React attaches onLoad, so
  // check `complete` when the node mounts too.
  const measure = useCallback((node: HTMLImageElement | null) => {
    if (node?.complete && node.naturalWidth > 0) setLoaded(true);
  }, []);

  return (
    <img
      ref={measure}
      className={loaded ? 'is-loaded' : undefined}
      src={`${THUMB_BASE}/${photo.file}`}
      alt={alt}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      onLoad={() => setLoaded(true)}
    />
  );
};

interface PhotoGridProps {
  photos: GalleryPhoto[];
  /** 'grid' fills the container; 'strip' scrolls horizontally (home page). */
  layout?: 'grid' | 'strip';
  /**
   * Lazy loading is right for the long gallery grid, but wrong for the home
   * page strip: its tiles sit off to the right, so the browser never loads them
   * and the strip reads as empty until you scroll it sideways.
   */
  lazy?: boolean;
}

const PhotoGrid: React.FC<PhotoGridProps> = ({ photos, layout = 'grid', lazy = layout === 'grid' }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const close = useCallback(() => setOpenIndex(null), []);
  const step = useCallback(
    (delta: number) =>
      setOpenIndex((current) =>
        current === null ? null : (current + delta + photos.length) % photos.length
      ),
    [photos.length]
  );

  useEffect(() => {
    if (openIndex === null) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openIndex, close, step]);

  const open = openIndex !== null ? photos[openIndex] : null;

  return (
    <>
      <div className={layout === 'strip' ? 'gallery-strip' : 'gallery-grid'}>
        {photos.map((photo, index) => (
          <button
            key={photo.file}
            type="button"
            className="gallery-tile"
            onClick={() => setOpenIndex(index)}
            aria-label={`Open ${captionFor(photo, index)}`}
          >
            <Thumb
              photo={photo}
              alt={captionFor(photo, index)}
              eager={!lazy || index < EAGER_COUNT}
            />
          </button>
        ))}
      </div>

      {open && openIndex !== null && (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={captionFor(open, openIndex)}
          onClick={close}
        >
          <button
            type="button"
            className="lightbox__btn lightbox__btn--close"
            onClick={close}
            aria-label="Close photo"
          >
            &times;
          </button>

          {photos.length > 1 && (
            <button
              type="button"
              className="lightbox__btn lightbox__btn--prev"
              onClick={(event) => {
                event.stopPropagation();
                step(-1);
              }}
              aria-label="Previous photo"
            >
              &#8249;
            </button>
          )}

          <img
            className="lightbox__img"
            src={`${FULL_BASE}/${open.file}`}
            alt={captionFor(open, openIndex)}
            onClick={(event) => event.stopPropagation()}
          />

          {photos.length > 1 && (
            <button
              type="button"
              className="lightbox__btn lightbox__btn--next"
              onClick={(event) => {
                event.stopPropagation();
                step(1);
              }}
              aria-label="Next photo"
            >
              &#8250;
            </button>
          )}

          <p className="lightbox__counter">
            {openIndex + 1} / {photos.length}
          </p>
        </div>
      )}
    </>
  );
};

export default PhotoGrid;
